import { useState, useMemo, useEffect, useCallback } from 'react';
import { CONSTANTS } from '../data.js';
import { eraIndexByKey } from '../lib/utils';

const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Basic type check to avoid simple errors
      if (typeof parsed === typeof fallback && !Array.isArray(parsed) && typeof parsed !== 'object') {
        return parsed;
      }
      if (Array.isArray(parsed) && Array.isArray(fallback)) {
        return parsed as T;
      }
      if (parsed !== null && typeof parsed === 'object' && typeof fallback === 'object') {
        return parsed;
      }
    }
  } catch {}
  return fallback;
};

export const usePlayerProgress = () => {
  const [discovered, setDiscovered] = useState<string[]>(() => {
    const saved = loadFromStorage<string[]>('time_alchemist_discovered', []);
    if (saved.length > 0) {
      return saved.filter(k => CONSTANTS.elements[k]);
    }
    return [...CONSTANTS.startingElements];
  });

  const [unlockedEras, setUnlockedEras] = useState<string[]>(() => {
    const saved = loadFromStorage<string[]>('time_alchemist_unlocked_eras', []);
    if (saved.length > 0) {
      const valid = new Set(CONSTANTS.eras.list.map(e => e.key));
      return saved.filter((k: string) => valid.has(k));
    }
    return ['primordial_age'];
  });

  const [completedCollections, setCompletedCollections] = useState<string[]>(() => 
    loadFromStorage<string[]>('time_alchemist_completed_collections', [])
  );

  const [completedTutorials, setCompletedTutorials] = useState<string[]>(() =>
    loadFromStorage<string[]>('time_alchemist_completed_tutorials', [])
  );

  const [playerAvatar, setPlayerAvatar] = useState<string>(() => 
    loadFromStorage<string>('time_alchemist_player_avatar', CONSTANTS.player.defaultAvatarKey)
  );

  const [playerDeck, setPlayerDeck] = useState<string[]>(() =>
    loadFromStorage<string[]>('time_alchemist_player_deck', [])
  );

  useEffect(() => {
    localStorage.setItem('time_alchemist_discovered', JSON.stringify(discovered));
  }, [discovered]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_unlocked_eras', JSON.stringify(unlockedEras));
  }, [unlockedEras]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_completed_collections', JSON.stringify(completedCollections));
  }, [completedCollections]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_completed_tutorials', JSON.stringify(completedTutorials));
  }, [completedTutorials]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_player_avatar', JSON.stringify(playerAvatar));
  }, [playerAvatar]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_player_deck', JSON.stringify(playerDeck));
  }, [playerDeck]);

  const currentEraKey = useMemo(() => {
    const sortedUnlocked = [...unlockedEras].sort((a, b) => eraIndexByKey[b] - eraIndexByKey[a]);
    return sortedUnlocked[0] || CONSTANTS.eras.list[0].key;
  }, [unlockedEras]);

  const currentEraName = useMemo(() => {
    const e = CONSTANTS.eras.list.find(er => er.key === currentEraKey);
    return e ? e.name : CONSTANTS.eras.list[0].name;
  }, [currentEraKey]);

  const resetPlayerProgress = useCallback(() => {
    const starting = [...CONSTANTS.startingElements];
    setDiscovered(starting);
    setUnlockedEras(['primordial_age']);
    setCompletedCollections([]);
    setCompletedTutorials([]);
    setPlayerAvatar(CONSTANTS.player.defaultAvatarKey);
    setPlayerDeck([]);
    try {
      localStorage.setItem('time_alchemist_discovered', JSON.stringify(starting));
      localStorage.setItem('time_alchemist_unlocked_eras', JSON.stringify(['primordial_age']));
      localStorage.setItem('time_alchemist_completed_collections', JSON.stringify([]));
      localStorage.setItem('time_alchemist_completed_tutorials', JSON.stringify([]));
      localStorage.setItem('time_alchemist_player_avatar', JSON.stringify(CONSTANTS.player.defaultAvatarKey));
      localStorage.setItem('time_alchemist_player_deck', JSON.stringify([]));
    } catch {}
  }, []);

  return {
    discovered,
    setDiscovered,
    unlockedEras,
    setUnlockedEras,
    completedCollections,
    setCompletedCollections,
    completedTutorials,
    setCompletedTutorials,
    playerAvatar,
    setPlayerAvatar,
    playerDeck,
    setPlayerDeck,
    currentEraKey,
    currentEraName,
    resetPlayerProgress,
  };
};
